// @owners { team: patient-health }
import { COLORS } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  AltoIcon,
  AltoSpinningLoader,
  Description,
  PressableAltoIcon,
  Row,
  XxsSpacing,
} from '@alto/design-system';
import React, { useContext } from 'react';
import { openModal } from '~shared/actions/modal';
import { reasonIsRefillTooSoon } from '~shared/features/delivery/helpers';
import { formattedNextAvailableDate } from '~shared/features/next-available-date/helpers/formattedNextAvailableDate';
import { useNextAvailableDatesForPrescriptions } from '~shared/features/next-available-date/queries/useNextAvailableDatesForPrescriptions';
import { type OriginName } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared } from '~shared/store';
import { type Prescription } from '~shared/types/clients';
import { GetItSoonerActionSheet } from '~web/features/my-meds/components/modals/GetItSoonerActionSheet';

type Props = {
  readonly origin?: OriginName;
  readonly prescription: Prescription;
};

export const NextAvailableDateInfoRow = ({ origin, prescription }: Props) => {
  const dispatch = useDispatchShared();
  const { setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const { isLoading, nextAvailableDate } = useNextAvailableDatesForPrescriptions({
    prescriptionID: prescription.id,
  });

  if (isLoading) {
    return (
      <Row>
        <AltoIcon
          name="calendar-small"
          type="grey"
        />
        <AltoSpinningLoader />
      </Row>
    );
  }

  if (!nextAvailableDate?.earliest) return null;

  const earliest = nextAvailableDate.earliest;
  const handleOpenModal = () => {
    const isRefillTooSoon = reasonIsRefillTooSoon(earliest.reason);

    if (isRefillTooSoon) {
      setActiveActionSheet(
        <GetItSoonerActionSheet
          onClose={closeActionSheet}
          prescription={prescription}
          origin={origin || ''}
        />,
      );
    } else {
      dispatch(openModal('NEXT_AVAILABLE_DATE_MODAL', { origin, prescription }));
    }
  };

  return (
    <Row rowStart>
      <AltoIcon
        name="calendar-small"
        type="grey"
      />
      <XxsSpacing />
      <Description
        fontFamily={!earliest.date ? 'italic' : undefined}
        color={COLORS.TEXT_COLORS.GREY}
      >
        {formattedNextAvailableDate(earliest.date, earliest.reason)}
      </Description>
      <XxsSpacing />
      <PressableAltoIcon
        name="info-small"
        onPress={handleOpenModal}
        accessibilityLabel="Press to view availability options for this prescription"
      />
    </Row>
  );
};
